import { useMemo } from 'react';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export interface UseHighlightedVerticalLinesProps {
  verticalLines: VerticalLine[];
  selectedMarkerTimestamp?: string | null;
  selectedTimestampsSet?: Set<string>;
}

/**
 * Creates a highlighted version of vertical lines for selected markers
 * Applies purple color to markers matching by ID
 */
export function useHighlightedVerticalLines({
  verticalLines,
  selectedMarkerTimestamp,
  selectedTimestampsSet,
}: UseHighlightedVerticalLinesProps): VerticalLine[] {
  return useMemo(() => {
    if (!selectedMarkerTimestamp && (!selectedTimestampsSet || selectedTimestampsSet.size === 0)) {
      return verticalLines;
    }

    return verticalLines.map((line: VerticalLine) => {
      // Check if this line is selected by ID
      const isSelectedById = line.id && selectedTimestampsSet && selectedTimestampsSet.has(line.id);

      // Fallback: check by timestamp for single marker selection
      let isSelectedByTimestamp = false;
      if (selectedMarkerTimestamp && line.timestamp) {
        const lineTimestamp =
          line.timestamp instanceof Date
            ? line.timestamp.toISOString()
            : typeof line.timestamp === 'number'
            ? new Date(line.timestamp).toISOString()
            : String(line.timestamp);
        isSelectedByTimestamp = lineTimestamp === selectedMarkerTimestamp;
      }

      // If this line is selected, apply purple highlight
      if (isSelectedById || isSelectedByTimestamp) {
        return {
          ...line,
          color: '#7071F3', // Purple highlight color
          originalColor: line.color,
        };
      }

      return line;
    });
  }, [verticalLines, selectedMarkerTimestamp, selectedTimestampsSet]);
}
